import { type SortingState } from "@tanstack/react-table";
import { ColumnVisibilityItem } from "providers/Auth";
import { CreatedWithinOption } from "../components/FiltersDrawers/CreatedWithinOption";
import {
  DEFAULT_SORTING,
  LEADS_COLUMNS_TAB_KEY,
  LeadSortColumnId,
  LeadView
} from "../constants";
import { LeadsFilters, LeadViewColumns, LeadViewFilters } from "../types";

interface CaptureViewStateInput {
  filters: LeadsFilters | undefined;
  search: string;
  starred: boolean;
  sorting: SortingState;
  assigneeIds: number[] | null;
  view: LeadView;
  columnsVisibility: ColumnVisibilityItem[];
}

const isValidSortColumn = (value: string | undefined): boolean =>
  !!value && (Object.values(LeadSortColumnId) as string[]).includes(value);

const isValidCreatedWithin = (
  value: string | undefined
): value is CreatedWithinOption =>
  !!value &&
  (Object.values(CreatedWithinOption) as string[]).includes(value) &&
  value !== CreatedWithinOption.None;

/**
 * Build the opaque `filters`/`columns` payloads from the current Leads state.
 * Empty/default values are omitted so a saved view stays a minimal,
 * round-trip-faithful snapshot of what is actually applied.
 */
export const captureViewState = ({
  filters,
  search,
  starred,
  sorting,
  assigneeIds,
  view,
  columnsVisibility
}: CaptureViewStateInput): {
  filters: LeadViewFilters;
  columns: LeadViewColumns;
} => {
  const captured: LeadViewFilters = {};

  if (search.trim()) captured.search = search.trim();
  if (starred) captured.starred = true;
  // Only store Pipeline; List is the default, so omitting it keeps the
  // snapshot minimal and backward-compatible with pre-existing views.
  if (view === LeadView.Pipeline) captured.view = view;
  if (filters?.properties?.length) captured.properties = filters.properties;
  if (filters?.stages?.length) captured.stages = filters.stages;
  if (filters?.leadSources?.length) captured.lead_sources = filters.leadSources;
  if (filters?.activityTypes?.length)
    captured.activity_types = filters.activityTypes;
  if (isValidCreatedWithin(filters?.createdWithin))
    captured.created_within = filters?.createdWithin;
  if (filters?.createdAfter) captured.created_after = filters.createdAfter;
  if (filters?.createdBefore) captured.created_before = filters.createdBefore;
  if (assigneeIds?.length) captured.assignee_ids = assigneeIds;

  const sort = sorting[0];
  if (sort) {
    captured.sort_by = sort.id;
    captured.sort_dir = sort.desc ? "desc" : "asc";
  }

  return {
    filters: captured,
    columns: { [LEADS_COLUMNS_TAB_KEY]: columnsVisibility }
  };
};

/**
 * Canonicalize a view's filter blob for equivalence comparison. The multi-select
 * filter arrays are sets — their element order reflects the order the user
 * clicked options, not meaning — so "stage A then B" must compare equal to
 * "stage B then A". Sort each by a stable key before comparing. Columns are left
 * untouched here: their array order is meaningful display order.
 *
 * Note `view` (layout) is kept: it IS part of a view's identity, so switching
 * List⇄Pipeline off a saved view reads as a deviation. The "clean board" case
 * (toggling layout with no filters) is handled separately in the controller's
 * dirty-state check, which ignores layout when comparing against the Default
 * baseline.
 */
export const normalizeViewFilters = (
  filters: LeadViewFilters | undefined
): LeadViewFilters => {
  if (!filters) return {};
  const result: LeadViewFilters = { ...filters };
  const byId = (a: { id: string }, b: { id: string }) =>
    a.id.localeCompare(b.id);
  if (result.properties) result.properties = [...result.properties].sort(byId);
  if (result.stages) result.stages = [...result.stages].sort(byId);
  if (result.lead_sources)
    result.lead_sources = [...result.lead_sources].sort(byId);
  if (result.activity_types)
    result.activity_types = [...result.activity_types].sort();
  if (result.assignee_ids)
    result.assignee_ids = [...result.assignee_ids].sort((a, b) => a - b);
  return result;
};

/** Rebuild the Leads `LeadsFilters` state from a stored view blob. */
export const viewFiltersToLeadsFilters = (
  view: LeadViewFilters
): LeadsFilters | undefined => {
  const result: LeadsFilters = {};

  if (view.properties?.length) result.properties = view.properties;
  if (view.stages?.length) result.stages = view.stages;
  if (view.lead_sources?.length) result.leadSources = view.lead_sources;
  if (view.activity_types?.length) result.activityTypes = view.activity_types;
  if (isValidCreatedWithin(view.created_within))
    result.createdWithin = view.created_within;
  if (view.created_after) result.createdAfter = view.created_after;
  if (view.created_before) result.createdBefore = view.created_before;

  return Object.keys(result).length ? result : undefined;
};

/** Rebuild the table sorting state from a stored view blob. */
export const viewSortingToSortingState = (
  view: LeadViewFilters
): SortingState => {
  if (!isValidSortColumn(view.sort_by) || !view.sort_by) return DEFAULT_SORTING;
  return [{ id: view.sort_by, desc: view.sort_dir === "desc" }];
};
